import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { ThreadAttachment, JSONRequestBlock, JSONBlock, JSONUrlsResponseBlock, JSONResponseBlock, JSONApiBlockResponse } from "@preveil-api";
import {
  Account, Attachment, usePostStorageDownloadProxyMutation, useAppDispatch, Helpers, useAppSelector, decryptAttachment, Message, usePostStorageDownloadDirectMutation, PostStorageDownloadApiResponse,
  MessageAnchors, FileSizeLimits, getIDChunks, FetchS3AttachmentBlocks, MessageHandlerDisplayType, MailErrorMessages, DriveSuccessMessages
} from "src/common";
import { Icon } from "src/components";
import { appActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  collection_id: string;
  thread_attachments: ThreadAttachment[];
  active_attachment: boolean;
  is_web?: boolean;
  handleAttachmentModal: (_attachments: Attachment[]) => void;
};

function AttachmentBulkSave(props: AllProps) {
  const { is_web, current_account, thread_attachments, collection_id, handleAttachmentModal } = props;
  const dispatch = useAppDispatch();
  const mail_direct_fetch = useAppSelector((state: RootState) => state.app.mail_direct_fetch);
  const [filtered_attachments, setFilteredAttachments] = useState<ThreadAttachment[] | undefined>();
  const [is_loading, setIsLoading] = useState<boolean>(false);
  const [getMessageBlocksProxy] = usePostStorageDownloadProxyMutation();
  const [getMessageBlocksDirect] = usePostStorageDownloadDirectMutation();

  // Description: Only include filtered
  useEffect(() => {
    const _filtered = thread_attachments.filter((attachment: ThreadAttachment) => { return attachment.block_ids && attachment.block_ids.length > 0; });
    setFilteredAttachments(_filtered);
  }, []);

  // ------------------------------------------------------------------
  // App Build mode:
  // ------------------------------------------------------------------
  // Description: On click to download all files and open Attachment Modal with blobs populated
  function handleDownloadAllFiles() {
    setIsLoading(true);
    const filtered = thread_attachments.filter((attachment: ThreadAttachment) => { return attachment.block_ids && attachment.block_ids.length > 0; });
    if (!!filtered && filtered.length > 0) {
      Promise.all(
        _.map(filtered, (attachment: ThreadAttachment) => {
          const blocks: JSONRequestBlock = {
            collection_id,
            block_id: attachment.content_reference_id,
          };
          return handleFetchWebBlocks(blocks, attachment);
        }))
        .then((decrypted_attachments: Attachment[]) => {
          if (decrypted_attachments.length > 0) {
            // NOTE: Download files directly for web into users device
            is_web ?
              handleDownloadSuccess(decrypted_attachments.length) :
              handleAttachmentModal(decrypted_attachments);
          } else {
            handleDownloadErrors(MailErrorMessages.error_loading_attachment);
          }
        })
        .catch(() => {
          // NOTE: Errors are handled per-attachment in handleFetchWebBlocks / handleDownloadErrors
          console.error("Error downloading files");
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      handleDownloadErrors(MailErrorMessages.error_saving_all_attachment);
    }

  }

  // Description: Need to break up the block_id in max of MAIL_MAX_BLOCKS_PER_REQUEST per each request
  function handleFetchWebBlocks(blocks: JSONRequestBlock, attachment: ThreadAttachment, retry: boolean = false): Promise<Attachment> {
    const is_direct = mail_direct_fetch && !retry;
    const block_ids = blocks.block_id.split(",");
    const chunks: string[][] = getIDChunks(block_ids, FileSizeLimits.MAIL_MAX_BLOCKS_PER_REQUEST);
    // NOTE: Iterate all calls and assemble blocks
    return Promise.all(
      _.map(chunks, (chunk: string[]) => {
        const block_chunk: JSONRequestBlock[] = _.map(chunk, (id: string) => ({ collection_id: blocks.collection_id, block_id: id }));
        // NOTE: Choose to call direct first if not allowed call Proxy
        return is_direct ? handleWebGetBlocksDirect(block_chunk) : handleWebGetBlocksProxy(block_chunk);
      }),
    )
      .then((responses: JSONBlock[][]) => {
        const result: JSONBlock[] = _.flatten(responses);
        return handleGetBlockDataSuccess(result, attachment);
      })
      .catch((error: unknown) => {
        if (is_direct) {
          return handleDirectDownloadErrors(blocks, attachment, error);
        }
        handleDownloadErrors(
          MailErrorMessages.error_downloading_attachment.replace(MessageAnchors.file_name, attachment.filename),
          error,
        );
        return Promise.reject(error);
      });
  }

  // Description: Try to get blocks from S3 directly first - If failed fallback to Proxy call
  async function handleWebGetBlocksDirect(blocks: JSONRequestBlock[]): Promise<JSONBlock[]> {
    return await getMessageBlocksDirect({
      account_ids: Account.getAccountIdentifiers(current_account),
      body: { blocks },
    }).unwrap()
      .then(async (data: PostStorageDownloadApiResponse) => {
        return await Promise.all(
          _.map(data.urls, (block_response: JSONUrlsResponseBlock) => {
            return FetchS3AttachmentBlocks(block_response.block_url, block_response.block_id);
          }),
        );
      });
  }

  // Description: Get Mail through Collection Server as Proxy if Direct call fails
  async function handleWebGetBlocksProxy(blocks: JSONRequestBlock[]): Promise<JSONBlock[]> {
    return await getMessageBlocksProxy({
      account_ids: Account.getAccountIdentifiers(current_account),
      body: { blocks },
    }).unwrap()
      .then((response: JSONApiBlockResponse) => {
        if (!!response.blocks && response.blocks.length > 0) {
          return _.map(response.blocks, (block: JSONResponseBlock) => {
            return {
              block_id: block.block_id,
              data: Helpers.b64Decode(block.data),
            };
          });
        } else { // Note: this will trigger catch block
          throw new Error("Failed to retrieve blocks from Proxy call");
        }
      });
  }

  // Description: handle returned data from either direct call or proxy
  function handleGetBlockDataSuccess(blocks: JSONBlock[], attachment: ThreadAttachment): Promise<Attachment> {
    return decryptAttachment(attachment, blocks)
      .then((decrypted_attachment: Attachment) => {
        if (decrypted_attachment.blob) {
          decrypted_attachment.loadContentFromBlob(decrypted_attachment.blob);
          // NOTE: Save to computer if this is web build
          !!is_web && decrypted_attachment.saveToComputer();
          return decrypted_attachment;
        } else { // Note: this will trigger catch block
          throw new Error("Failed to decrypted attachment");
        }
      })
      .catch((error: unknown) => {
        handleDownloadErrors(
          MailErrorMessages.error_downloading_attachment.replace(MessageAnchors.file_name, attachment.filename),
          error,
        );
        return Promise.reject(error);
      });
  }

  // ------------------------------------------------------------------
  // Callback Functions:
  // ------------------------------------------------------------------
  // Description: After download clean up
  function handleDownloadSuccess(item_count: number) {
    setIsLoading(false);
    const message = DriveSuccessMessages.success_bulk_downloading.replace(MessageAnchors.item_count, item_count.toString());
    dispatch(uiActions.handleSetMessage(new Message(message, MessageHandlerDisplayType.toastr)));
  }

  // Description: After download clean up
  function handleDownloadErrors(error_message: string, stack?: unknown, is_toastr: boolean = true) {
    const msg = error_message;
    setIsLoading(false);
    dispatch(
      uiActions.handleRequestErrors(
        new Message(msg, is_toastr ? MessageHandlerDisplayType.toastr : MessageHandlerDisplayType.logger),
        stack,
      ),
    );
  }

  // Description: Handle Downloads directly error, set store value and try Proxy
  //              Handle Direct call Failure - set flag to not use again in this session
  function handleDirectDownloadErrors(blocks: JSONRequestBlock, attachment: ThreadAttachment, stack?: unknown): Promise<Attachment> {
    dispatch(appActions.setMailDirectFetch(false));
    const msg = MailErrorMessages.error_downloading_attachment.replace(MessageAnchors.file_name, attachment.filename);
    dispatch(uiActions.handleRequestErrors(new Message(msg, MessageHandlerDisplayType.logger), stack));
    setTimeout(() => (dispatch(uiActions.handleMessageDismiss())), 1000);
    return handleFetchWebBlocks(blocks, attachment, true);
  }

  // NOTE: The Save All button only works for Desktop App
  // TO DO: Use Download component for Web (DA-7579)
  return (!!filtered_attachments && filtered_attachments.length > 0) ?
    <div className="btn-panel text-end mt-2">
      <Button
        variant="outline-secondary"
        size="sm"
        disabled={is_loading}
        onClick={handleDownloadAllFiles}
        aria-label={is_web ? "Download all attachments in this message." : "Save all attachments to Drive"}
        data-tooltip-id="pv-tooltip"
        data-tooltip-content={is_web ? "Download all attachments in this message." : "Save all attachments to Drive"}>
        {is_loading ? <i className="spinner"></i> : <Icon className="ficon-download" />} Save All
      </Button>
    </div> : <></>;
}

export default React.memo(AttachmentBulkSave);
